import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  MarkEmailRead as MarkAllReadIcon,
  DeleteSweep as ClearAllIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as DeliveredIcon,
  Info as InfoIcon,
  AccountBalanceWallet as WalletIcon,
  Delete as DeleteIcon,
  RadioButtonUnchecked as UnreadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../../hooks/useNotifications';
import { resolveNotificationMessage } from '../../../utils/notificationMessages';
import moment from 'moment';

const getCustomerNotificationRoute = (notification) => {
  switch (notification.type) {
    case 'package_update':
      return '/myparcels';
    case 'wallet_topup_request':
    case 'wallet_topup_approved':
    case 'wallet_topup_rejected':
      return '/wallet/requests';
    default:
      return null;
  }
};

const getNotificationIcon = (notification) => {
  switch (notification.type) {
    case 'package_update':
      return notification.station?.finalStation || notification.status?.toLowerCase() === 'delivered'
        ? <DeliveredIcon color="success" />
        : <ShippingIcon color="secondary" />;
    case 'wallet_topup_request':
    case 'wallet_topup_approved':
    case 'wallet_topup_rejected':
      return <WalletIcon color="secondary" />;
    default:
      return <InfoIcon color="info" />;
  }
};

const NotificationCard = ({ notification, onMarkAsRead, onDelete, onClick }) => {
  const theme = useTheme();

  const handleClick = () => {
    if (!notification.isRead) onMarkAsRead(notification.id);
    onClick(notification);
  };

  return (
    <Card
      sx={{
        mb: 1.5,
        cursor: 'pointer',
        backgroundColor: notification.isRead ? 'transparent' : 'rgba(109, 40, 217, 0.06)',
        border: notification.isRead
          ? `1px solid ${theme.palette.divider}`
          : `2px solid ${theme.palette.secondary.main}`,
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: theme.shadows[3],
        },
      }}
      onClick={handleClick}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" alignItems="flex-start" gap={1.5}>
          <Box mt={0.5}>{getNotificationIcon(notification)}</Box>
          <Box flex={1} minWidth={0}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Typography
                variant="body2"
                sx={{ fontWeight: notification.isRead ? 'normal' : 'bold', flex: 1, pr: 1 }}
              >
                {resolveNotificationMessage(notification)}
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                {!notification.isRead && (
                  <Chip label="New" size="small" color="secondary" sx={{ height: 16, fontSize: '0.65rem' }} />
                )}
                <Typography variant="caption" color="text.secondary">
                  {moment(notification.timestamp).fromNow()}
                </Typography>
              </Box>
            </Box>

            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
              {notification.tracking && (
                <Chip
                  label={notification.tracking}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              )}
              {notification.station && (
                <Chip
                  label={`${notification.station.name}${notification.station.finalStation ? ' ✓' : ''}`}
                  size="small"
                  variant="outlined"
                  color={notification.station.finalStation ? 'success' : 'default'}
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              )}
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" gap={0.5}>
            {!notification.isRead && (
              <Tooltip title="Mark as read">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onMarkAsRead(notification.id); }}
                  sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                >
                  <UnreadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onDelete(notification.id); }}
                sx={{ opacity: 0.6, '&:hover': { opacity: 1, color: 'error.main' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const CustomerNotifications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    notifications,
    unreadCount,
    isConnected,
    isLoading,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    refreshNotifications,
  } = useNotifications();

  const [tabValue, setTabValue] = useState(0);
  const [filtered, setFiltered] = useState([]);
  const [deleted, setDeleted] = useState(new Set());

  const visible = notifications.filter(n => !deleted.has(n.id));

  useEffect(() => {
    switch (tabValue) {
      case 0:
        setFiltered(visible);
        break;
      case 1:
        setFiltered(visible.filter(n => !n.isRead));
        break;
      case 2:
        setFiltered(visible.filter(n => n.type === 'package_update'));
        break;
      default:
        setFiltered(visible);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, tabValue, deleted]);

  const handleNotificationClick = (notification) => {
    const route = getCustomerNotificationRoute(notification);
    if (route) navigate(route);
  };

  const handleDelete = (id) => {
    setDeleted(prev => new Set([...prev, id]));
  };

  const handleClearAll = () => {
    clearNotifications();
    setDeleted(new Set());
  };

  const emptyMessage = tabValue === 1
    ? t('notifications.noUnread')
    : t('notifications.noNotifications');

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 2.5,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: 'primary.contrastText',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {t('notifications.title')}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {t('notifications.subtitle')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Alert
              severity={isConnected ? 'success' : 'error'}
              variant="filled"
              sx={{
                py: 0.25,
                '& .MuiAlert-message': { fontSize: '0.8rem' },
                backgroundColor: isConnected ? 'success.dark' : 'error.dark',
              }}
            >
              {isConnected ? t('notifications.connected') : t('notifications.disconnected')}
            </Alert>
            <Tooltip title={t('common.refresh')}>
              <IconButton color="inherit" onClick={refreshNotifications} disabled={isLoading}>
                {isLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Controls */}
      <Paper sx={{ mb: 2.5 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" px={1.5}>
            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons="auto"
            >
              <Tab label={`${t('notifications.all')} (${visible.length})`} />
              <Tab label={`${t('notifications.unread')} (${unreadCount})`} />
              <Tab label={t('notifications.packageUpdates')} />
            </Tabs>
            <Box display="flex" gap={0.5}>
              <Tooltip title={t('notifications.markAllRead')}>
                <span>
                  <IconButton size="small" onClick={markAllAsRead} disabled={unreadCount === 0}>
                    <MarkAllReadIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('notifications.clearAll')}>
                <span>
                  <IconButton size="small" onClick={handleClearAll} disabled={visible.length === 0}>
                    <ClearAllIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Content */}
      {isLoading && visible.length === 0 ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {emptyMessage}
          </Typography>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RefreshIcon />}
            onClick={refreshNotifications}
            disabled={isLoading}
            sx={{ mt: 1 }}
          >
            {t('common.refresh')}
          </Button>
        </Paper>
      ) : (
        <Box>
          {filtered.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onMarkAsRead={markAsRead}
              onDelete={handleDelete}
              onClick={handleNotificationClick}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CustomerNotifications;
